import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Buildings, Plus, Pencil, Trash, ArrowsClockwise, MagnifyingGlass, X, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPO_LABEL = { aseguradora: "Aseguradora", particular: "Particular" };
const TIPO_CLS = {
  aseguradora: "bg-blue-50 text-blue-700 border-blue-200",
  particular: "bg-purple-50 text-purple-700 border-purple-200",
};

const empty = () => ({
  tipo: "aseguradora",
  nombre_fiscal: "",
  cif_nif: "",
  direccion: "",
  cp: "",
  ciudad: "",
  provincia: "",
  pais: "España",
  email: "",
  telefono: "",
  persona_contacto: "",
  iban: "",
  condiciones_pago_dias: 30,
  iva_default_pct: 21,
  irpf_default_pct: 0,
  etiquetas: [],
  notas: "",
  active: true,
});

export default function ClientesFacturacion() {
  const [params, setParams] = useSearchParams();
  const focusId = params.get("focus");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (tipo) params.set("tipo", tipo);
      const r = await api.get(`/facturacion/clientes${params.toString() ? `?${params}` : ""}`);
      setItems(r.data || []);
      // Auto-focus si viene desde Gabinete
      if (focusId) {
        const target = (r.data || []).find((c) => c.id === focusId);
        if (target) setEditing(target);
        const next = new URLSearchParams(params);
        next.delete("focus");
        setParams(next, { replace: true });
      }
    } catch (e) {
      console.error(e); toast.error("No se pudieron cargar los clientes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSave = async (data) => {
    try {
      if (data.id) {
        await api.put(`/facturacion/clientes/${data.id}`, data);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/facturacion/clientes", data);
        toast.success("Cliente creado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const onDelete = async (cli) => {
    if (!window.confirm(`¿Eliminar al cliente "${cli.nombre_fiscal}"?`)) return;
    try {
      await api.delete(`/facturacion/clientes/${cli.id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const importFromAseguradoras = async () => {
    try {
      const r = await api.post("/facturacion/clientes/import-from-aseguradoras");
      toast.success(`Importados ${r.data?.creados ?? 0} clientes desde Aseguradoras`);
      load();
    } catch (e) { toast.error("Error al importar"); }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight inline-flex items-center gap-2">
            <Buildings className="w-6 h-6 text-brand" weight="duotone" />
            Clientes de facturación
          </h1>
          <p className="text-xs text-muted mt-1">Aseguradoras y particulares a los que el gabinete factura.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="cf-import-aseg"
            onClick={importFromAseguradoras}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:border-border-strong bg-white"
          >
            <DownloadSimple className="w-3.5 h-3.5" weight="bold" /> Importar de Aseguradoras
          </button>
          <button
            data-testid="cf-new"
            onClick={() => setEditing(empty())}
            className="btn-brand inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md font-semibold"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo cliente
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 card-elevated p-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" weight="bold" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="cf-search"
            placeholder="Buscar por nombre, CIF o código..."
            className="!pl-9 !text-xs"
          />
        </div>
        <select
          value={tipo}
          onChange={(e) => { setTipo(e.target.value); setTimeout(load, 0); }}
          data-testid="cf-filter-tipo"
          className="!text-xs"
        >
          <option value="">Todos los tipos</option>
          <option value="aseguradora">Aseguradoras</option>
          <option value="particular">Particulares</option>
        </select>
        <button onClick={load} className="text-xs px-3 py-2 rounded-md border border-border hover:border-border-strong bg-white inline-flex items-center gap-1.5">
          <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Recargar
        </button>
      </div>

      {/* Tabla */}
      <div className="card-elevated overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">Sin clientes. Crea el primero.</div>
        ) : (
          <table className="w-full text-sm" data-testid="cf-table">
            <thead className="thead-modern">
              <tr>
                <th>Código</th>
                <th>Tipo</th>
                <th>Nombre fiscal</th>
                <th>CIF/NIF</th>
                <th>Ciudad</th>
                <th>Pago</th>
                <th className="!text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => (
                <tr key={c.id} className={`border-b border-[#F1F3F5] hover:bg-[#F5F6F8] transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}>
                  <td className="px-3 py-2.5 text-[12.5px] tabular-nums">{c.codigo}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${TIPO_CLS[c.tipo]}`}>{TIPO_LABEL[c.tipo]}</span>
                  </td>
                  <td className="px-3 py-2.5 font-medium">{c.nombre_fiscal}</td>
                  <td className="px-3 py-2.5 text-[12.5px] tabular-nums">{c.cif_nif || "—"}</td>
                  <td className="px-3 py-2.5 text-xs">{c.ciudad || "—"}</td>
                  <td className="px-3 py-2.5 text-xs">{c.condiciones_pago_dias}d</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => setEditing(c)} className="text-muted hover:text-brand p-1.5" data-testid={`cf-edit-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" weight="bold" />
                    </button>
                    <button onClick={() => onDelete(c)} className="text-muted hover:text-rose-600 p-1.5" data-testid={`cf-del-${c.id}`}>
                      <Trash className="w-3.5 h-3.5" weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ClienteModal initial={editing} onClose={() => setEditing(null)} onSave={onSave} />
      )}
    </div>
  );
}

function ClienteModal({ initial, onClose, onSave }) {
  const [data, setData] = useState(initial);
  const [tags, setTags] = useState((initial.etiquetas || []).join(", "));

  const setF = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!data.nombre_fiscal) { toast.error("Falta nombre fiscal"); return; }
    onSave({
      ...data,
      etiquetas: tags.split(",").map((s) => s.trim()).filter(Boolean),
      condiciones_pago_dias: Number(data.condiciones_pago_dias) || 30,
      iva_default_pct: Number(data.iva_default_pct) || 0,
      irpf_default_pct: Number(data.irpf_default_pct) || 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-3xl shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">{data.id ? "Editar cliente" : "Nuevo cliente"}</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-foreground p-1"><X className="w-4 h-4" weight="bold"/></button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Tipo">
              <select value={data.tipo} onChange={(e) => setF("tipo", e.target.value)} className="!text-xs" data-testid="cf-form-tipo">
                <option value="aseguradora">Aseguradora</option>
                <option value="particular">Particular</option>
              </select>
            </Field>
            <Field label="Nombre fiscal *">
              <input value={data.nombre_fiscal} onChange={(e) => setF("nombre_fiscal", e.target.value)} className="!text-xs" data-testid="cf-form-nombre" />
            </Field>
            <Field label="CIF / NIF">
              <input value={data.cif_nif || ""} onChange={(e) => setF("cif_nif", e.target.value)} className="!text-xs" data-testid="cf-form-cif" />
            </Field>
            <Field label="Email">
              <input type="email" value={data.email || ""} onChange={(e) => setF("email", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="Teléfono">
              <input value={data.telefono || ""} onChange={(e) => setF("telefono", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="Persona de contacto">
              <input value={data.persona_contacto || ""} onChange={(e) => setF("persona_contacto", e.target.value)} className="!text-xs" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><Field label="Dirección">
              <input value={data.direccion || ""} onChange={(e) => setF("direccion", e.target.value)} className="!text-xs" />
            </Field></div>
            <Field label="CP">
              <input value={data.cp || ""} onChange={(e) => setF("cp", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="Ciudad">
              <input value={data.ciudad || ""} onChange={(e) => setF("ciudad", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="Provincia">
              <input value={data.provincia || ""} onChange={(e) => setF("provincia", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="País">
              <input value={data.pais || "España"} onChange={(e) => setF("pais", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="IBAN">
              <input value={data.iban || ""} onChange={(e) => setF("iban", e.target.value)} className="!text-xs" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Pago (días)">
              <input type="number" value={data.condiciones_pago_dias} onChange={(e) => setF("condiciones_pago_dias", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="IVA por defecto (%)">
              <input type="number" step="0.01" value={data.iva_default_pct} onChange={(e) => setF("iva_default_pct", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="IRPF por defecto (%)">
              <input type="number" step="0.01" value={data.irpf_default_pct} onChange={(e) => setF("irpf_default_pct", e.target.value)} className="!text-xs" />
            </Field>
          </div>

          <Field label="Etiquetas (separadas por coma)">
            <input value={tags} onChange={(e) => setTags(e.target.value)} className="!text-xs" placeholder="Ej: VIP, mensual" />
          </Field>
          <Field label="Notas">
            <textarea value={data.notas || ""} onChange={(e) => setF("notas", e.target.value)} rows={2} className="!text-xs" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold" data-testid="cf-form-save">Guardar</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted block mb-1">{label}</span>
      {children}
    </label>
  );
}
